import React, { ReactNode, ReactNodeArray } from 'react';
import { Grid } from '@material-ui/core';
import { omit } from 'lodash/fp';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { FormSectionInfo } from 'components/form/components';
import { useStepper } from '../../utils/use-stepper';
import { CUSTOMER_FORM_ID } from '../../global/constants';
import { Utils } from '../../utils';
import {
  BuyProcessFormContextInterface,
  BuyProcessFormContextProvider,
  BuyProcessStep,
} from './buy-process-form-utils';
import { SubmitButton } from './components/submit-button';
import { BuyProcessFormComponent } from './buy-process-form-types';
import { BuyProcessFormClientInfo } from './components/client-info';
import { BuyProcessFormCompanyDetails } from './components/company-details';
import { BuyProcessFormOwnerShares } from './components/owner-shares';
import { BuyProcessFormReservation } from './components/reservation';
import { BuyProcessFormRepresentatives } from './components/representatives';
import { BuyProcessFormFiscalYear } from './components/fiscal-year';
import { BuyProcessFormAuditor } from './components/auditor';
import { BuyProcessFormProxies } from './components/proxies';
import { BuyProcessFormCostStatement } from './components/cost-statement';
import { BuyProcessFormSummary } from './components/summary';
import { BuyProcessFormStepper } from './components/stepper/stepper';
import { PublicFacingFormWrapper } from './components/buy-process-form-wrapper/public-facing-form-wrapper';
import { BuyProcessHooks } from './index';

const useStyles = makeStyles((theme) => ({
  stepContainer: {
    '& form > *:not(:last-child)': {
      marginBottom: theme.spacing(6),
    },
  },
}));

const BuyProcessStepCustomer = omit('submissionInfo', BuyProcessStep);
export const BuyProcessForm: BuyProcessFormComponent = () => {
  const {
    activeStep,
    lastActiveStep,
    nextStep,
    getStepInfo,
    setRequestedStep,
    goToRequestedStep,
  } = useStepper({
    steps: Object.keys(BuyProcessStepCustomer).length,
  });

  Utils.Hooks.useWarnOnLeave();
  const { municipalities, isVerticalStepper } = BuyProcessHooks.useBuyProcessFormSetup(activeStep, CUSTOMER_FORM_ID);

  const value: BuyProcessFormContextInterface = React.useMemo(
    () => ({
      handleNextStep: nextStep,
      municipalities,
      id: CUSTOMER_FORM_ID,
      goToRequestedStep,
      setRequestedStep,
    }),
    [goToRequestedStep, municipalities, nextStep, setRequestedStep],
  );

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <BuyProcessFormClientInfo />;
      case 1:
        return <BuyProcessFormCompanyDetails />;
      case 2:
        return <BuyProcessFormOwnerShares />;
      case 3:
        return <BuyProcessFormReservation />;
      case 4:
        return <BuyProcessFormRepresentatives />;
      case 5:
        return <BuyProcessFormFiscalYear />;
      case 6:
        return <BuyProcessFormAuditor />;
      case 7:
        return <BuyProcessFormProxies />;
      case 8:
        return <BuyProcessFormCostStatement />;
      case 9:
        return <BuyProcessFormSummary />;
      default:
        return null;
    }
  };

  return (
    <PublicFacingFormWrapper>
      <BuyProcessFormContextProvider value={value}>
        <BuyProcessFormStepper
          activeStep={activeStep}
          lastActiveStep={lastActiveStep}
          steps={BuyProcessStepCustomer}
          setRequestedStep={setRequestedStep}
          getStepInfo={getStepInfo}
        >
          {isVerticalStepper && (
            <BuyProcessForm.Content>
              {getStepContent(activeStep)}
            </BuyProcessForm.Content>
          )}
        </BuyProcessFormStepper>
        <FormSectionInfo.InfoBox style={{ marginBottom: 8 }} hasArrow={false}>
          Du kan redigera tidigare steg i formuläret genom att klicka i listan
          ovan.
        </FormSectionInfo.InfoBox>
        {!isVerticalStepper && (
          <BuyProcessForm.Content>
            {getStepContent(activeStep)}
          </BuyProcessForm.Content>
        )}
      </BuyProcessFormContextProvider>
    </PublicFacingFormWrapper>
  );
};

BuyProcessForm.SubmitButton = SubmitButton;

export type BuyProcessFormContentProps = {
  children: ReactNode | ReactNodeArray;
};
BuyProcessForm.Content = function BuyProcessFormContent(
  props: BuyProcessFormContentProps,
) {
  const classes = useStyles();

  return (
    <Grid className={classes.stepContainer} style={{ marginTop: '16px' }}>
      {props.children}
    </Grid>
  );
};
